import { motion } from 'framer-motion';
import type { Translate } from 'next-translate';
import React from 'react';
import { type DropzoneOptions } from 'react-dropzone';
import { Container } from '@/components/elements/Container';
import { MyImage } from '@/components/elements/MyImage';
import { useEmailFunnelFlow, useTranslationWithLocale } from '@/hooks';
import { GenerateLandingLinkService, GoogleService } from '@/services';
import { type LandingConfigItem } from '@/types';
import { GOOGLE_EVENTS } from '@/constants';
import { pictureMotion } from '@/animations';
import { LandingListItem } from '../../blocks/LandingListItem';
import { DropzoneCtaButton } from '../../reusableSections';
import { EmailPopup } from '../EmailPopup';
import styles from './WhyLift.module.scss';

interface WhyLiftProps {
	data: LandingConfigItem['whyLift'];
	t: Translate;
	withDropzone: boolean;
	dropzoneOptions: DropzoneOptions;
	funnelLinkService?: string;
	useEmailFlow?: boolean;
}
export const WhyLift: React.FC<WhyLiftProps> = ({
	data,
	t,
	withDropzone,
	dropzoneOptions,
	funnelLinkService,
	useEmailFlow = false,
}) => {
	const { t: tCommon } = useTranslationWithLocale('common');
	const { open: openEmailPopup, popupProps } = useEmailFunnelFlow({
		funnelLinkService,
		t,
		tCommon,
	});

	const goToWebProduct = () => {
		if (useEmailFlow) {
			openEmailPopup();
			return;
		}
		GoogleService.logEvent(GOOGLE_EVENTS.LANDING_PASSED);
		window.location.href =
			funnelLinkService || GenerateLandingLinkService.webApp();
	};
	// Destructuring
	const { title, images, list, buttonText } = data;

	return (
		<section className={`${styles['section']}`}>
			<Container className={`container ${styles['section__container']}`}>
				<div className={`${styles['section__top']}`}>
					<h2 className={`${styles['section__title']}`}>{t(title)}</h2>
				</div>

				<div className={styles['section__info']}>
					<motion.div
						className={`${styles['section__image-container']}`}
						{...pictureMotion}
					>
						<MyImage
							imageProps={{
								src: images[0].image,
								alt: t(images[0].alt),
								title: t(images[0].title),
								loading: 'lazy',
							}}
							settings={{
								classes: {
									picture: `${styles['section__picture']}`,
									image: `${styles['section__image']}`,
								},
							}}
						/>
					</motion.div>

					<div className={`${styles['section__list-wrapper']}`}>
						<ul className={`${styles['section__list']}`}>
							{list.map(item => (
								<LandingListItem key={item.title} item={item} t={t} />
							))}
						</ul>
						<DropzoneCtaButton
							withDropzone={withDropzone}
							dropzoneOptions={dropzoneOptions}
							text={t(buttonText)}
							onClick={goToWebProduct}
							classes={{
								dropzoneButton: styles['section__drop-wrapper-button'],
								defaultButton: styles['section__button'],
								dropzone: styles['section__drop-wrapper'],
							}}
						/>
					</div>
				</div>
			</Container>
			{useEmailFlow && <EmailPopup {...popupProps} />}
		</section>
	);
};
